import { ClipboardCheck } from 'lucide-react'
import {
  BagDelegate,
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'
import { RunbookToolMessage } from '../ScreenataChatToolMessages'

const completedIcon = (
  <ClipboardCheck className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

const RUNBOOK_FIELD_EXCLUDE = new Set([
  'id',
  'title',
  'name',
  'description',
  'scoringCriteria',
  'executionConfig',
])

function scalarFields(record: Record<string, unknown>) {
  return Object.entries(record)
    .filter(
      ([key, value]) => !RUNBOOK_FIELD_EXCLUDE.has(key) && formatToolValue(value)
    )
    .slice(0, 24)
    .map(([key, value]) => ({
      label: key,
      value: formatToolValue(value),
      tone: normalizeTone(value),
    }))
}

function stepsSection(steps: unknown): string | null {
  if (!Array.isArray(steps) || steps.length === 0) return null
  return steps
    .slice(0, 12)
    .map((step, index) => {
      if (!step || typeof step !== 'object') return `${index + 1}. ${String(step)}`
      const record = step as Record<string, unknown>
      const title =
        pickString(record.title, record.name, record.instruction, record.label) ??
        `Step ${index + 1}`
      const action =
        Array.isArray(record.actions) && record.actions.length > 0
          ? `\n   - Actions: ${record.actions
              .map((item) =>
                item && typeof item === 'object'
                  ? pickString(
                      (item as Record<string, unknown>).tool,
                      (item as Record<string, unknown>).action
                    )
                  : formatToolValue(item)
              )
              .filter(Boolean)
              .join(', ')}`
          : ''
      return `${index + 1}. ${title}${action}`
    })
    .join('\n')
}

// ── runbookSearch ────────────────────────────────────────────────────────
export function RunbookSearchToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const runbooks: Array<Record<string, unknown>> = Array.isArray(
    state.result?.runbooks
  )
    ? state.result.runbooks
    : []
  const count =
    typeof state.result?.count === 'number'
      ? state.result.count
      : runbooks.length
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Searching procedures...'
        completedLabel={`Found ${plural(count, 'procedure')} available`}
        loadingDetail={pickString(state.args?.query)}
        completedDetail={count === 0 ? null : pickString(state.args?.query)}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No procedures found'
          items={runbooks.map((runbook) => {
            const title = pickString(runbook.title, runbook.name) ?? 'Untitled procedure'
            return {
              id: null,
              title,
              subtitle: pickString(runbook.description),
              meta: [
                Array.isArray(runbook.providers)
                  ? runbook.providers.join(', ')
                  : formatToolValue(runbook.providers),
                Array.isArray(runbook.controls)
                  ? runbook.controls.join(', ')
                  : formatToolValue(runbook.controls),
              ],
            }
          })}
          moreLabel={state.result?.truncated ? 'more results available' : null}
        />
      ) : null}
    </>
  )
}

// ── runbookGet ───────────────────────────────────────────────────────────
export function RunbookGetToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const template =
    state.result?.template && typeof state.result.template === 'object'
      ? (state.result.template as Record<string, unknown>)
      : null
  const title = pickString(
    template?.title,
    state.result?.title,
    state.args?.id
  )
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading procedure...'
        completedLabel='Procedure loaded'
        loadingDetail={pickString(state.args?.id, state.args?.templateId)}
        completedDetail={title}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) && template ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(part, `runbook-${title ?? 'detail'}`)}
          payload={{
            entityType: 'runbook',
            toolName: 'runbookGet',
            title: title ?? 'Untitled procedure',
            description: pickString(template.description),
            fields: scalarFields(template),
            sections: [
              {
                title: 'Instructions',
                content:
                  template.scoringCriteria &&
                  typeof template.scoringCriteria === 'object'
                    ? pickString(
                        (template.scoringCriteria as Record<string, unknown>)
                          .instructions
                      )
                    : null,
              },
              { title: 'Steps', content: stepsSection(state.result?.steps) },
            ],
          }}
        />
      ) : null}
    </>
  )
}

// ── runbookUpdate ────────────────────────────────────────────────────────
export function RunbookUpdateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Updating procedure...'
      completedLabel='Procedure updated'
      completedDetail={pickString(state.result?.message, state.args?.templateId)}
      completedIcon={completedIcon}
    />
  )
}

// ── runbookEdit ──────────────────────────────────────────────────────────
export function RunbookEditToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Editing runbook...'
      completedLabel='Runbook updated'
      completedDetail={pickString(state.result?.message, state.args?.runbookId)}
      completedIcon={completedIcon}
    />
  )
}

// ── runbookImprove ───────────────────────────────────────────────────────
export function RunbookImproveToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Reviewing runbook...'
      completedLabel='Review complete'
      completedDetail={pickString(state.result?.summary, state.args?.runbookId)}
      completedIcon={completedIcon}
    />
  )
}

// ── runbookLink ─────────────────────────────────────────────────────────
export function RunbookLinkToolMessage(props: ToolPartProps) {
  return <BagDelegate Component={RunbookToolMessage} action='link' {...props} />
}
